import { useState } from "react";
import { captureError } from "@/lib/sentry";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LANDING_COPY } from "@/i18n/landing";
import LangToggle from "@/components/LangToggle";
import CompanionDemo from "@/components/landing/CompanionDemo";
import CompanionPillars from "@/components/landing/CompanionPillars";
import FanStories from "@/components/landing/FanStories";
import {
  LightningIcon,
  ChartLineUpIcon,
  ChatCircleDotsIcon,
  TrophyIcon,
  BroadcastIcon,
  CardsThreeIcon,
  MicrophoneIcon,
  WaveformIcon,
  PlayCircleIcon,
} from "@phosphor-icons/react";

const HERO_BG = "https://images.unsplash.com/photo-1679391029864-d46f366a456b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxzdGFkaXVtJTIwbGlnaHRzJTIwZGFyayUyMG5pZ2h0fGVufDB8fHx8MTc4MDA2OTUyNHww&ixlib=rb-4.1.0&q=85";

const FAN_ICONS = [BroadcastIcon, ChatCircleDotsIcon, TrophyIcon, CardsThreeIcon, ChartLineUpIcon, LightningIcon];
const PRESS_ICONS = [BroadcastIcon, MicrophoneIcon, WaveformIcon];
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");
// Test ids stay stable across languages by using the English titles.
const FAN_IDS = LANDING_COPY.en.features.items.map((f) => slug(f.title));
const PRESS_IDS = LANDING_COPY.en.press.items.map((f) => slug(f.title));

export default function Landing() {
  const { setUser } = useAuth();
  const { lang } = useLang();
  const c = LANDING_COPY[lang];
  const navigate = useNavigate();
  const [demoLoading, setDemoLoading] = useState(false);

  const startDemo = async (dest = "/press-box") => {
    setDemoLoading(true);
    try {
      await api.post("/auth/demo");
      const me = await api.get("/auth/me");
      setUser(me.data);
      navigate(dest, { replace: true });
    } catch (err) {
      captureError("demo start failed:", err);
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-50 bg-[#050505]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-1.5 shrink-0">
            <img src="/gamly-logo.png" alt="Gamly AI" className="h-7 sm:h-9 w-auto" />
            <span className="hidden sm:inline text-[9px] font-black uppercase tracking-[0.12em] text-[#0A84FF] bg-[#0A84FF]/12 border border-[#0A84FF]/30 rounded px-1 py-0.5 leading-none" data-testid="beta-badge">Beta</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <LangToggle />
            <Link to="/pricing" data-testid="nav-pricing-landing" className="hidden sm:inline text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white">
              {c.nav.pricing}
            </Link>
            <button
              onClick={() => startDemo()}
              disabled={demoLoading}
              data-testid="nav-demo-landing"
              className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#FDB927]/40 text-[#FDB927] text-xs font-bold uppercase tracking-wider hover:bg-[#FDB927]/10 disabled:opacity-50"
            >
              <PlayCircleIcon weight="fill" /> {demoLoading ? c.nav.loading : c.nav.demo}
            </button>
            <Link
              to="/login"
              data-testid="nav-signin-landing"
              className="px-3 sm:px-4 py-2 rounded-md bg-[#0A84FF] text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 transition-colors whitespace-nowrap"
            >
              {c.nav.signin}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-50" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#050505]/60 to-[#050505]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full bg-[#FF0000]/10 border border-[#FF0000]/40">
              <span className="live-dot" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF453A]" data-testid="hero-kicker">{c.hero.kicker}</span>
            </div>
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase max-w-3xl leading-[0.92]" data-testid="hero-headline">
              {c.hero.headA} <span className="text-[#0A84FF]">{c.hero.headB}</span>.
            </h1>
            <p className="mt-4 font-display font-bold text-xl md:text-2xl uppercase tracking-tight text-white/80" data-testid="hero-subheadline">
              {c.hero.sub1} <span className="text-[#FDB927]">{c.hero.sub2}</span>
            </p>
            <p className="mt-6 max-w-xl text-base md:text-lg text-white/65 font-medium" data-testid="hero-description">{c.hero.desc}</p>
            <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 [&>*]:whitespace-nowrap">
              <button
                onClick={() => startDemo()}
                disabled={demoLoading}
                data-testid="cta-live-demo"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black font-black uppercase tracking-wide transition-colors disabled:opacity-50"
              >
                <PlayCircleIcon weight="fill" /> {demoLoading ? c.hero.demoLoading : c.hero.demo}
              </button>
              <Link
                to="/login"
                data-testid="cta-get-started"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white font-black uppercase tracking-wide transition-colors"
              >
                <LightningIcon weight="fill" /> {c.hero.google}
              </Link>
              <Link
                to="/pricing"
                data-testid="cta-see-pricing"
                className="inline-flex items-center justify-center px-2 py-3 text-white/70 hover:text-white underline-offset-4 hover:underline font-black uppercase tracking-wide transition-colors"
              >
                {c.hero.pricing}
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3 text-xs font-bold uppercase tracking-[0.15em] text-white/40" data-testid="hero-sport-tags">
              {c.hero.tags.map((t) => <span key={t}>{t}</span>)}
            </div>
          </div>
          <CompanionDemo />
        </div>
      </section>

      {/* Why an AI Sports Companion */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="section-why-companion">
        <div className="mb-10 max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-2">{c.why.kicker}</p>
          <h2 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">{c.why.title}</h2>
        </div>
        <CompanionPillars />
      </section>

      {/* Fan stories + stats */}
      <section className="border-t border-white/10 bg-[#080808]" data-testid="section-fan-stories">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#32D74B] mb-2">{c.stories.kicker}</p>
              <h2 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">{c.stories.title}</h2>
            </div>
            <button
              onClick={() => startDemo("/watch/mlb_demo")}
              disabled={demoLoading}
              data-testid="cta-stories-demo"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white font-black uppercase tracking-wide transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              <PlayCircleIcon weight="fill" /> {demoLoading ? c.hero.demoLoading : c.stories.cta}
            </button>
          </div>
          <FanStories />
        </div>
      </section>

      {/* Companion features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#32D74B] mb-2">{c.features.kicker}</p>
          <h2 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">{c.features.title}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {c.features.items.map((f, i) => {
            const Icon = FAN_ICONS[i];
            return (
              <div key={FAN_IDS[i]} data-testid={`feature-card-${FAN_IDS[i]}`} className="p-6 rounded-md bg-[#121212] border border-white/10 hover:border-white/25 transition-colors">
                <Icon weight="bold" size={28} className="text-[#0A84FF] mb-4" />
                <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-2">{f.title}</h3>
                <p className="text-sm text-white/60 font-medium">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* For Sports Media */}
      <section className="border-t border-white/10 bg-gradient-to-br from-[#0A0A0A] via-[#0F0E08] to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FDB927] mb-2 inline-flex items-center gap-1.5">
              <BroadcastIcon weight="fill" /> {c.press.kicker}
            </p>
            <h2 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">{c.press.title}</h2>
            <p className="text-white/65 max-w-2xl mt-3 text-sm md:text-base">{c.press.desc}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {c.press.items.map((f, i) => {
              const Icon = PRESS_ICONS[i];
              return (
                <div key={PRESS_IDS[i]} data-testid={`press-feature-${PRESS_IDS[i]}`} className="p-6 rounded-md bg-[#121212] border border-[#FDB927]/20 hover:border-[#FDB927]/50 transition-colors">
                  <Icon weight="bold" size={28} className="text-[#FDB927] mb-4" />
                  <h3 className="font-display font-bold text-xl uppercase tracking-tight mb-2">{f.title}</h3>
                  <p className="text-sm text-white/60 font-medium">{f.desc}</p>
                </div>
              );
            })}
          </div>
          <button
            onClick={() => startDemo()}
            disabled={demoLoading}
            data-testid="cta-press-demo"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#FDB927] hover:bg-yellow-400 text-black font-black uppercase tracking-wide transition-colors disabled:opacity-50"
          >
            <PlayCircleIcon weight="fill" /> {demoLoading ? c.hero.demoLoading : c.press.cta}
          </button>
        </div>
      </section>

      {/* Why It Matters CTA */}
      <section className="border-t border-white/10 bg-gradient-to-r from-[#0A84FF]/10 via-transparent to-[#32D74B]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <h2 className="font-display font-bold text-3xl md:text-5xl uppercase tracking-tighter mb-4">
            {c.wim.a} <span className="text-[#0A84FF]">{c.wim.b}</span> {c.wim.c}
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8">{c.wim.desc}</p>
          <Link
            to="/login"
            data-testid="cta-bottom"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white font-black uppercase tracking-wide transition-colors"
          >
            <LightningIcon weight="fill" /> {c.wim.cta}
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <img src="/gamly-logo.png" alt="Gamly AI" className="h-7 w-auto" />
            <span className="text-[8px] font-black uppercase tracking-[0.12em] text-[#0A84FF] bg-[#0A84FF]/12 border border-[#0A84FF]/30 rounded px-1 py-0.5 leading-none">Beta</span>
          </div>
          <div className="text-xs text-white/40 font-medium" data-testid="landing-footer">{c.footer}</div>
        </div>
      </footer>
    </div>
  );
}
